
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DayOfWeek } from '@/models/Schedule';
import { toast } from '@/components/ui/sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { TimeSlotInsert, TimeSlotUpdate } from './types';

export const useTimeSlotMutations = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  // Create time slot mutation
  const createTimeSlotMutation = useMutation({
    mutationFn: async (timeSlotData: Omit<TimeSlotInsert, 'organization_id'>) => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID not found');
      }

      const dataWithOrgId = {
        ...timeSlotData,
        organization_id: currentOrganization.id,
      };

      const { data, error } = await supabase
        .from('time_slots')
        .insert(dataWithOrgId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeSlots', currentOrganization?.id] });
      toast.success(t("schedule.createSuccess"));
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : "Failed to create time slot");
    },
  });

  // Update time slot mutation
  const updateTimeSlotMutation = useMutation({
    mutationFn: async ({ id, ...updateData }: TimeSlotUpdate) => {
      const { data, error } = await supabase
        .from('time_slots')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeSlots', currentOrganization?.id] });
      toast.success(t("schedule.updateSuccess"));
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : "Failed to update time slot");
    },
  });

  // Delete time slot mutation
  const deleteTimeSlotMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('time_slots')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeSlots', currentOrganization?.id] });
      toast.success(t("schedule.deleteSuccess"));
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : "Failed to delete time slot");
    },
  });

  // Bulk operations
  const createMultipleTimeSlots = useMutation({
    mutationFn: async (timeSlotsData: Omit<TimeSlotInsert, 'organization_id'>[]) => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID not found');
      }

      const dataWithOrgId = timeSlotsData.map(slot => ({
        ...slot,
        organization_id: currentOrganization.id,
      }));

      const { data, error } = await supabase
        .from('time_slots')
        .insert(dataWithOrgId)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeSlots', currentOrganization?.id] });
      toast.success(t("schedule.createSuccess"));
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : "Failed to create time slots");
    },
  });

  const clearDayTimeSlots = useMutation({
    mutationFn: async (day: DayOfWeek) => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID not found');
      }

      const { error } = await supabase
        .from('time_slots')
        .update({ is_active: false })
        .eq('day_of_week', day)
        .eq('organization_id', currentOrganization.id)
        .eq('is_active', true);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeSlots', currentOrganization?.id] });
      toast.success(t("schedule.timeSlotsCleared"));
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : "Failed to clear time slots");
    },
  });

  const clearAllTimeSlots = useMutation({
    mutationFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID not found');
      }

      const { error } = await supabase
        .from('time_slots')
        .update({ is_active: false })
        .eq('organization_id', currentOrganization.id)
        .eq('is_active', true);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeSlots', currentOrganization?.id] });
      toast.success(t("schedule.timeSlotsCleared"));
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : "Failed to clear all time slots");
    },
  });

  return {
    createTimeSlot: createTimeSlotMutation.mutateAsync,
    updateTimeSlot: updateTimeSlotMutation.mutateAsync,
    deleteTimeSlot: deleteTimeSlotMutation.mutateAsync,
    createMultipleTimeSlots: createMultipleTimeSlots.mutateAsync,
    clearDayTimeSlots: clearDayTimeSlots.mutateAsync,
    clearAllTimeSlots: clearAllTimeSlots.mutateAsync,
    isCreating: createTimeSlotMutation.isPending,
    isUpdating: updateTimeSlotMutation.isPending,
    isDeleting: deleteTimeSlotMutation.isPending,
  };
};
